import { Koi } from '@ksyos/koi';
import { OsasComplaintBurden, OsasPressureUnit, OsasTrialPeriod, YesOrNoOrUnknown } from '@common/enums';
import { OsasPapType } from '@common/enums/osasPapType';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import {
    allTreatmentValidationDraft,
    allTreatmentValidationFinal,
} from '../osasAllTreatmentsSchemas/osasAllTreatmentsSchema';

const lifeStyleValidation = {
    patientFollowsLifestylePolicy: Koi.koi().enum(YesOrNoOrUnknown).allow(null),
};

const MRATreatmentValidation = {
    patientFollowsMraPolicy: Koi.koi().enum(YesOrNoOrUnknown).allow(null),
};

const SPTTreamentValidation = {
    sptAverageUse: getTextAreaStringSchema(true, 255),
    sptNightsWithSupport: getTextAreaStringSchema(true, 255),
    sptRemark: getTextAreaStringSchema(true),
    sptReportEndDate: Koi.koi().date().allow(null),
    sptReportStartDate: Koi.koi().date().allow(null),
    supinePositionFrom: getTextAreaStringSchema(true, 255),
    supinePositionTo: getTextAreaStringSchema(true, 255),
};

const pressureSchema = Koi.object().keys({
    unit: Koi.koi().enum(OsasPressureUnit).allow(null),
    value: getTextAreaStringSchema(true, 255),
});

const PAPTreatmentValidation = {
    centralApnea: getTextAreaStringSchema(true, 255),
    cheyneStokes: getTextAreaStringSchema(true, 255),
    extraValuesReport: getTextAreaStringSchema(true, 255),
    obstructiveApnea: getTextAreaStringSchema(true, 255),
    oxygenSaturation: getTextAreaStringSchema(true, 255),
    pAhi3Percent: getTextAreaStringSchema(true, 255),
    papAverageUse: getTextAreaStringSchema(true, 255),
    papNightsWithSupport: getTextAreaStringSchema(true, 255),
    papReportEndDate: Koi.koi().date().allow(null),
    papReportStartDate: Koi.koi().date().allow(null),
    pressureApapMaximum: pressureSchema,
    pressureApapMinimum: pressureSchema,
    pressureApapNinetyfive: pressureSchema,
    pressureCpap: pressureSchema,
    pressureEpap: pressureSchema,
    pressureIpap: pressureSchema,
    type: Koi.koi().enum(OsasPapType).allow(null),
};

const medicalHistoryValidation = {
    complaintBurden: Koi.koi().enum(OsasComplaintBurden).allow(null),
    hadContactWithPatient: Koi.boolean().allow(null),
    medicalHistory: getTextAreaStringSchema(true),
    remark: getTextAreaStringSchema(true),
    therapyStartDate: Koi.koi().todayOrBefore().allow(null),
    trialPeriod: Koi.koi().enum(OsasTrialPeriod).allow(null),
};

const policyValidation = {
    followUpEvaluationWeeks: Koi.number().integer().min(1).max(52).allow(null),
    policy: getTextAreaStringSchema(true),
};

const informValidation = {
    shouldNotifyGeneralPractitioner: Koi.boolean().allow(null),
};

export const treatmentChoiceSchema = Koi.object({
    MRA: Koi.boolean(),
    PAP: Koi.boolean(),
    SPT: Koi.boolean(),
    lifestyle: Koi.boolean(),
});

export const osasEvaluationSchema = {
    assets: getProcessActivityAssetsSchema(),
    treatingPhysicianPractitionerId: Koi.string().uuid().allow(null),
    treatmentChoice: treatmentChoiceSchema,
    ...lifeStyleValidation,
    ...MRATreatmentValidation,
    ...SPTTreamentValidation,
    ...PAPTreatmentValidation,
    ...medicalHistoryValidation,
    ...policyValidation,
    ...informValidation,
};

export const osasEvaluationDraftSchema = Koi.object({
    ...osasEvaluationSchema,
    allTreatments: allTreatmentValidationDraft,
});

export const osasEvaluationFinalSchema = Koi.object({
    ...osasEvaluationSchema,
    allTreatments: allTreatmentValidationFinal,
});
